// Export card of the chat info pages (mobile InfoExportPro): the three export
// features in one card, in the mobile order and row language:
//   Automatic chat export      switch; when on, the export address and Update
//   Export chat group data     timeframe, custom From / To, Export and progress (ChatExportMenuItem)
//   Request complete history   the address and Confirm
// The owners (pages/Chat/UserInfo, pages/Project/UserInfo) keep the state, the
// requests and the handlers; this card only draws them.
import React from "react";
import LanguageContext from "../../../helper/LanguageContext";
import ChatExportMenuItem from "../../ChatExportMenuItem";
import { InfoToggleRow, pressProps } from "./ChatInfoParts";
import "./InfoExportCard.css";

export const isValidEmailAddress = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());

/**
 * autoExport  { enabled, onToggle, email, onChangeEmail, savedEmail, emailVerified, onUpdate,
 *               infoOpen, onInfo, note, onRowClick, switchId } | null
 * pdf         { chat, openSignal } | null
 * history     { open, onToggle, email, onChangeEmail, onConfirm, loading, placeholder } | null
 */
const InfoExportCard = React.forwardRef(function InfoExportCard({ isInternal, autoExport, pdf, history }, ref) {
  const { strings } = React.useContext(LanguageContext);
  const t = (key, fallback) => (strings && strings[key]) || fallback;
  const parts = [];

  if (autoExport) {
    const verified = !!autoExport.emailVerified;
    const canUpdate =
      verified &&
      isValidEmailAddress(autoExport.email) &&
      String(autoExport.email || "") !== String(autoExport.savedEmail || "");
    parts.push(
      <InfoToggleRow
        key="auto"
        icon="schedule_send"
        label={t("automatic_chat_export", "Automatic chat export")}
        checked={!!autoExport.enabled}
        disabled={!verified}
        dim={!verified}
        onToggle={autoExport.onToggle}
        id={autoExport.switchId}
        isInternal={isInternal}
        infoOpen={!!autoExport.infoOpen}
        onInfo={autoExport.onInfo}
        note={autoExport.note}
        onRowClick={autoExport.onRowClick}
      >
        {autoExport.enabled ? (
          <div className="kjp-x-panel">
            <label className="kjp-x-field">
              <div className="kjp-x-label">{t("export_to_email", "Export to e-mail")}</div>
              <input
                type="email"
                className={`kjp-x-input${!verified || !isValidEmailAddress(autoExport.email) ? " is-incomplete" : ""}`}
                value={autoExport.email || ""}
                onChange={autoExport.onChangeEmail}
                placeholder={t("enter_email", "Enter e-mail")}
                disabled={!verified}
              />
            </label>
            <div className="kjp-x-actions">
              <button type="button" className="kj-btn kj-btn--primary" onClick={autoExport.onUpdate} disabled={!canUpdate}>
                {t("update", "Update")}
              </button>
            </div>
          </div>
        ) : null}
      </InfoToggleRow>
    );
  }

  if (pdf) {
    parts.push(<ChatExportMenuItem key="pdf" chat={pdf.chat} isInternal={isInternal} openSignal={pdf.openSignal} />);
  }

  if (history) {
    const canConfirm = isValidEmailAddress(history.email) && !history.loading;
    parts.push(
      <div key="history" className={`kjp-x-row${history.open ? " is-open" : ""}`}>
        <div className="kjp-info-row is-pressable" {...pressProps(history.onToggle)} aria-expanded={!!history.open}>
          <span className="material-symbols-outlined kjp-info-row__icon">history</span>
          <div className="kjp-info-row__label">{t("request_complete_chat_history", "Request complete chat history")}</div>
          <span className="material-symbols-outlined kjp-info-row__chevron">{history.open ? "keyboard_arrow_down" : "keyboard_arrow_right"}</span>
        </div>
        {history.open ? (
          <div className="kjp-x-panel">
            <label className="kjp-x-field">
              <div className="kjp-x-label">{t("email", "E-mail")}</div>
              <input
                type="email"
                className={`kjp-x-input${!isValidEmailAddress(history.email) ? " is-incomplete" : ""}`}
                value={history.email || ""}
                onChange={history.onChangeEmail}
                placeholder={history.placeholder}
              />
            </label>
            <div className="kjp-x-actions">
              <button
                type="button"
                className={`kj-btn kj-btn--primary${history.loading ? " kj-btn--loading" : ""}`}
                onClick={history.onConfirm}
                disabled={!canConfirm && !history.loading}
                aria-busy={!!history.loading}
              >
                {t("confirm", "Confirm")}
              </button>
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  if (!parts.length) return null;
  return (
    <div ref={ref} className="kjp-info-card kjp-x-card">
      {parts.map((part) => (
        <div key={part.key} className="kjp-info-card__item">
          {part}
        </div>
      ))}
    </div>
  );
});

export default InfoExportCard;
